/*
  Wednesday's Woe — behaviour for the enhanced layout blocks.
  Kept separate from theme.js so the core storefront runtime stays untouched.
  Every initialiser is idempotent and re-runs on shopify:section:load so the
  Theme Editor stays reliable.
*/
(() => {
  'use strict';

  const NS = window.WWBlocks || (window.WWBlocks = {});
  if (NS.ready) return;
  NS.ready = true;

  const countdownTimers = new Map();
  const marqueeObservers = new Map();

  function reducedMotion() {
    const query = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
    return query.matches || document.documentElement.classList.contains('motion-none');
  }

  function within(root, selector) {
    const found = [];
    if (root && root.nodeType === 1 && root.matches && root.matches(selector)) found.push(root);
    if (root && root.querySelectorAll) found.push(...root.querySelectorAll(selector));
    return found;
  }

  /* ---------------------------------------------------------------
     Countdown — a quiet, typographic timer rather than a loud clock.
     --------------------------------------------------------------- */
  function parseDeadline(value) {
    if (!value) return null;
    const trimmed = String(value).trim();
    // Accept "YYYY-MM-DD" and "YYYY-MM-DD HH:MM"; treat as local time.
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?$/);
    if (match) {
      const [, y, mo, d, h, mi] = match;
      const date = new Date(Number(y), Number(mo) - 1, Number(d), Number(h || 0), Number(mi || 0), 0, 0);
      return Number.isNaN(date.getTime()) || date.getFullYear() !== Number(y) || date.getMonth() !== Number(mo) - 1 || date.getDate() !== Number(d) || date.getHours() !== Number(h || 0) || date.getMinutes() !== Number(mi || 0) ? null : date;
    }
    const fallback = new Date(trimmed);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  function renderCountdown(root) {
    const deadline = countdownTimers.get(root)?.deadline;
    if (!deadline) return;
    const remaining = deadline.getTime() - Date.now();

    if (remaining <= 0) {
      root.dataset.expired = 'true';
      const message = root.dataset.expiredMessage || '';
      const output = root.querySelector('[data-countdown-output]');
      if (output) output.textContent = message;
      if (root.dataset.hideOnExpiry === 'true') {
        const host = root.closest('[data-ww-block]') || root;
        host.hidden = true;
      }
      stopCountdown(root);
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const parts = {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };

    Object.entries(parts).forEach(([unit, value]) => {
      root.querySelectorAll(`[data-countdown-unit="${unit}"]`).forEach((node) => {
        node.textContent = String(value).padStart(2, '0');
      });
    });
  }

  function stopCountdown(root) {
    const record = countdownTimers.get(root);
    if (record?.interval) window.clearInterval(record.interval);
    if (record) record.interval = 0;
  }

  function initCountdowns(scope) {
    within(scope, '[data-countdown]').forEach((root) => {
      stopCountdown(root);
      const deadline = parseDeadline(root.dataset.countdown);
      if (!deadline) {
        countdownTimers.delete(root);
        return;
      }
      countdownTimers.set(root, { deadline, interval: 0 });
      renderCountdown(root);
      const record = countdownTimers.get(root);
      if (record && !root.dataset.expired) {
        record.interval = window.setInterval(() => renderCountdown(root), 1000);
      }
    });
  }

  /* ---------------------------------------------------------------
     Dismissible bars — remembered for the session only.
     --------------------------------------------------------------- */
  function barStorageKey(bar) {
    return `ww-bar-dismissed-${bar.dataset.barId || 'default'}`;
  }

  function initDismissBars(scope) {
    within(scope, '[data-dismiss-bar]').forEach((bar) => {
      if (bar.dataset.wwBarReady === 'true') return;
      bar.dataset.wwBarReady = 'true';
      let dismissed = false;
      try {
        dismissed = window.sessionStorage.getItem(barStorageKey(bar)) === '1';
      } catch (_) {
        dismissed = false;
      }
      // The Theme Editor must always show the bar so it can be styled.
      const designMode = Boolean(window.Shopify && window.Shopify.designMode);
      if (dismissed && !designMode) bar.hidden = true;
    });
  }

  function dismissBar(bar) {
    bar.hidden = true;
    try {
      window.sessionStorage.setItem(barStorageKey(bar), '1');
    } catch (_) {
      // A blocked store never prevents dismissal for this pageview.
    }
  }

  /* ---------------------------------------------------------------
     Tabs — roving focus, arrow keys, single visible panel.
     --------------------------------------------------------------- */
  function activateTab(tabs, target, focus = false) {
    const buttons = Array.from(tabs.querySelectorAll('[data-tab-button]'));
    const panels = Array.from(tabs.querySelectorAll('[data-tab-panel]'));
    if (!buttons.length) return;

    const active = target && buttons.includes(target) ? target : buttons[0];
    buttons.forEach((button) => {
      const isActive = button === active;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.tabIndex = isActive ? 0 : -1;
      button.classList.toggle('is-active', isActive);
    });

    const activeId = active.getAttribute('aria-controls');
    panels.forEach((panel) => {
      const isActive = panel.id === activeId;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    if (focus) active.focus();
  }

  function initTabs(scope) {
    within(scope, '[data-tabs]').forEach((tabs) => {
      if (tabs.dataset.wwTabsReady === 'true') return;
      tabs.dataset.wwTabsReady = 'true';
      const buttons = Array.from(tabs.querySelectorAll('[data-tab-button]'));
      const preselected = buttons.find((button) => button.dataset.tabDefault === 'true');
      activateTab(tabs, preselected || buttons[0]);
    });
  }

  /* ---------------------------------------------------------------
     Scroll rails — arrows, and a dot index that tracks the nearest card.
     --------------------------------------------------------------- */
  function railCards(rail) {
    return Array.from(rail.querySelectorAll('[data-rail-item]'));
  }

  function updateRail(rail) {
    const shell = rail.closest('[data-rail-shell]');
    if (!shell) return;
    const cards = railCards(rail);
    if (!cards.length) return;

    const nearest = cards.reduce((best, card, index) => {
      const distance = Math.abs(card.offsetLeft - rail.scrollLeft);
      const bestDistance = Math.abs(cards[best].offsetLeft - rail.scrollLeft);
      return distance < bestDistance ? index : best;
    }, 0);

    shell.querySelectorAll('[data-rail-dot]').forEach((dot, index) => {
      const isActive = index === nearest;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    const maxScroll = rail.scrollWidth - rail.clientWidth - 1;
    shell.querySelectorAll('[data-rail-prev]').forEach((button) => {
      button.disabled = rail.scrollLeft <= 1;
    });
    shell.querySelectorAll('[data-rail-next]').forEach((button) => {
      button.disabled = rail.scrollLeft >= maxScroll;
    });
  }

  function scrollRail(rail, direction) {
    const cards = railCards(rail);
    if (!cards.length) return;
    const step = cards[0].getBoundingClientRect().width + 16;
    rail.scrollBy({ left: step * direction, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  function initRails(scope) {
    within(scope, '[data-rail]').forEach((rail) => {
      if (rail.dataset.wwRailReady === 'true') return;
      rail.dataset.wwRailReady = 'true';
      let frame = 0;
      rail.addEventListener('scroll', () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          updateRail(rail);
        });
      }, { passive: true });
      updateRail(rail);
    });
  }

  /* ---------------------------------------------------------------
     Stats — a restrained count-up, skipped entirely under reduced motion.
     --------------------------------------------------------------- */
  function runCount(node) {
    const target = Number.parseFloat(node.dataset.countTo);
    if (!Number.isFinite(target)) return;
    const decimals = Number.parseInt(node.dataset.countDecimals || '0', 10) || 0;
    const prefix = node.dataset.countPrefix || '';
    const suffix = node.dataset.countSuffix || '';

    if (reducedMotion()) {
      node.textContent = `${prefix}${target.toFixed(decimals)}${suffix}`;
      return;
    }

    const duration = 1100;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      node.textContent = `${prefix}${(target * eased).toFixed(decimals)}${suffix}`;
      if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }

  function initCounters(scope) {
    within(scope, '[data-count-to]').forEach((node) => {
      if (node.dataset.wwCountReady === 'true') return;
      node.dataset.wwCountReady = 'true';

      if (!('IntersectionObserver' in window)) {
        runCount(node);
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          runCount(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.4 });
      observer.observe(node);
    });
  }

  /* ---------------------------------------------------------------
     Marquee — duplicate the track so the loop is seamless.
     --------------------------------------------------------------- */
  function initMarquees(scope) {
    within(scope, '[data-marquee]').forEach((marquee) => {
      if (marquee.dataset.wwMarqueeReady === 'true') return;
      marquee.dataset.wwMarqueeReady = 'true';
      const track = marquee.querySelector('[data-marquee-track]');
      if (!track || track.children.length === 0) return;

      // A single duplicated run is enough for a continuous -50% translation.
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('data-marquee-track');
      track.parentElement.appendChild(clone);
      marquee.classList.add('is-ready');
    });
  }

  /* ---------------------------------------------------------------
     Table of contents — built from the headings of a target region.
     --------------------------------------------------------------- */
  function slugify(text, index) {
    const base = String(text).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return base ? `${base}-${index}` : `section-${index}`;
  }

  function initTocs(scope) {
    within(scope, '[data-toc]').forEach((toc) => {
      if (toc.dataset.wwTocReady === 'true') return;
      toc.dataset.wwTocReady = 'true';

      const targetSelector = toc.dataset.tocTarget || '.article-body, .rte';
      const list = toc.querySelector('[data-toc-list]');
      const region = document.querySelector(targetSelector);
      if (!list || !region) {
        toc.hidden = true;
        return;
      }

      const levels = (toc.dataset.tocLevels || 'h2,h3')
        .split(',')
        .map((level) => level.trim())
        .filter(Boolean)
        .join(',');
      const headings = Array.from(region.querySelectorAll(levels));
      if (!headings.length) {
        toc.hidden = true;
        return;
      }

      const fragment = document.createDocumentFragment();
      headings.forEach((heading, index) => {
        if (!heading.id) heading.id = slugify(heading.textContent || '', index);
        const item = document.createElement('li');
        item.className = `ww-toc__item ww-toc__item--${heading.tagName.toLowerCase()}`;
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent || '';
        item.appendChild(link);
        fragment.appendChild(item);
      });
      list.textContent = '';
      list.appendChild(fragment);
      toc.hidden = false;
    });
  }

  /* ---------------------------------------------------------------
     Delegated interaction
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     Mega menu — hover and focus are handled in CSS; this adds an explicit
     click for touch and keyboard users, plus outside-click dismissal.
     --------------------------------------------------------------- */
  function closeMegaMenus(except = null) {
    document.querySelectorAll('.ww-mega.is-open').forEach((mega) => {
      if (mega === except) return;
      mega.classList.remove('is-open');
      mega.querySelector('.ww-mega__trigger')?.setAttribute('aria-expanded', 'false');
    });
  }

  function initMegaMenus(root) {
    within(root, '.ww-mega').forEach((mega) => {
      if (mega.dataset.wwMegaReady) return;
      mega.dataset.wwMegaReady = 'true';
      const trigger = mega.querySelector('.ww-mega__trigger');
      trigger?.setAttribute('aria-expanded', mega.classList.contains('is-open') ? 'true' : 'false');
      mega.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch' || !window.matchMedia('(hover: hover)').matches) return;
        closeMegaMenus(mega);
        mega.classList.add('is-open');
        mega.dataset.hoverOpen = 'true';
        trigger?.setAttribute('aria-expanded', 'true');
      });
      mega.addEventListener('pointerleave', () => {
        mega.dataset.hoverOpen = 'false';
        if (!mega.contains(document.activeElement)) closeMegaMenus();
      });
      mega.addEventListener('focusout', (event) => {
        if (!mega.contains(event.relatedTarget)) closeMegaMenus();
      });
    });
  }

  function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const megaTrigger = target.closest('.ww-mega__trigger');
    if (megaTrigger) {
      event.preventDefault();
      const mega = megaTrigger.closest('.ww-mega');
      const willOpen = !mega.classList.contains('is-open') || mega.dataset.hoverOpen === 'true';
      mega.dataset.hoverOpen = 'false';
      closeMegaMenus(mega);
      mega.classList.toggle('is-open', willOpen);
      megaTrigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      return;
    }
    if (!target.closest('.ww-mega')) closeMegaMenus();

    const dismiss = target.closest('[data-dismiss-trigger]');
    if (dismiss) {
      event.preventDefault();
      const bar = dismiss.closest('[data-dismiss-bar]');
      if (bar) dismissBar(bar);
      return;
    }

    const tabButton = target.closest('[data-tab-button]');
    if (tabButton) {
      event.preventDefault();
      const tabs = tabButton.closest('[data-tabs]');
      if (tabs) activateTab(tabs, tabButton);
      return;
    }

    const railPrev = target.closest('[data-rail-prev]');
    const railNext = target.closest('[data-rail-next]');
    if (railPrev || railNext) {
      event.preventDefault();
      const shell = (railPrev || railNext).closest('[data-rail-shell]');
      const rail = shell?.querySelector('[data-rail]');
      if (rail) scrollRail(rail, railNext ? 1 : -1);
      return;
    }

    const railDot = target.closest('[data-rail-dot]');
    if (railDot) {
      event.preventDefault();
      const shell = railDot.closest('[data-rail-shell]');
      const rail = shell?.querySelector('[data-rail]');
      const cards = rail ? railCards(rail) : [];
      const card = cards[Number.parseInt(railDot.dataset.railDot, 10)];
      if (rail && card) {
        rail.scrollTo({ left: card.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }
  }

  function onKeydown(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (event.key === 'Escape' && document.querySelector('.ww-mega.is-open')) {
      const open = document.querySelector('.ww-mega.is-open');
      event.preventDefault();
      closeMegaMenus();
      open?.querySelector('.ww-mega__trigger')?.focus();
      return;
    }

    const tabButton = target.closest('[data-tab-button]');
    if (!tabButton) return;
    if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const tabs = tabButton.closest('[data-tabs]');
    const buttons = tabs ? Array.from(tabs.querySelectorAll('[data-tab-button]')) : [];
    if (!buttons.length) return;

    let index = buttons.indexOf(tabButton);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = buttons.length - 1;
    else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
    activateTab(tabs, buttons[index], true);
  }

  function init(scope = document) {
    try {
      initMegaMenus(scope);
      initCountdowns(scope);
      initDismissBars(scope);
      initTabs(scope);
      initRails(scope);
      initCounters(scope);
      initMarquees(scope);
      initTocs(scope);
    } catch (error) {
      if (window.console?.error) console.error('Wednesday Woe blocks failed to initialise.', error);
    }
  }

  function cleanup(scope) {
    within(scope, '[data-countdown]').forEach((root) => {
      stopCountdown(root);
      countdownTimers.delete(root);
    });
    within(scope, '[data-marquee]').forEach((marquee) => {
      marqueeObservers.get(marquee)?.disconnect();
      marqueeObservers.delete(marquee);
    });
  }

  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const tabs = target?.closest('[data-tabs]');
    const panel = target?.closest('[data-tab-panel]');
    const button = target?.closest('[data-tab-button]') || (panel && Array.from(tabs?.querySelectorAll('[data-tab-button]') || []).find(b => b.getAttribute('aria-controls') === panel.id));
    if (tabs && button) activateTab(tabs, button);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));
  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-rail]').forEach(updateRail);
  }, { passive: true });

  NS.init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }
})();
